import { useState, useEffect, useCallback } from 'react';
import { Download, Bell, Share, X } from 'lucide-react';
import { pushAPI } from '../services/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const hasPushSupport = typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function InstallPushButtons() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isInStandaloneMode());
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [pushDenied, setPushDenied] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Listen for install prompt (Chrome/Android/Desktop)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Check push subscription status
  useEffect(() => {
    if (!hasPushSupport) {
      setPushDenied(true);
      return;
    }
    if (Notification.permission === 'denied') {
      setPushDenied(true);
      return;
    }
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setIsPushSubscribed(!!sub);
      });
    });
  }, []);

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIOSModal(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const handleEnablePush = useCallback(async () => {
    if (!hasPushSupport) return;
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'denied') {
        setPushDenied(true);
        return;
      }
      if (perm !== 'granted') return;

      const registration = await navigator.serviceWorker.ready;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const subJson = subscription.toJSON();
      await pushAPI.subscribe({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        expiration_time: subJson.expirationTime || null,
      });

      setIsPushSubscribed(true);
    } catch (err) {
      console.error('Push subscription failed:', err);
    }
  }, []);

  return (
    <>
      {/* Install App Button */}
      {!isInstalled && (
        <Button
          data-testid="install-app-button"
          size="sm"
          variant="ghost"
          onClick={handleInstall}
          className="text-white hover:bg-gk-navy-hl gap-1.5"
          title="Install App"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline text-xs">Install</span>
        </Button>
      )}

      {/* Enable Push Notifications Button */}
      {!isPushSubscribed && !pushDenied && (
        <Button
          data-testid="enable-push-button"
          size="sm"
          variant="ghost"
          onClick={handleEnablePush}
          className="text-white hover:bg-gk-navy-hl gap-1.5"
          title="Enable Notifications"
        >
          <Bell className="w-4 h-4" />
          <span className="hidden sm:inline text-xs">Notify</span>
        </Button>
      )}

      {/* iOS Install Instructions Modal */}
      <Dialog open={showIOSModal} onOpenChange={setShowIOSModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="border-b border-slate-200 pb-4 -mx-6 -mt-2 px-6 mb-4">
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Download className="w-5 h-5 text-gk-navy" />
              Install GateKeeper Pro
            </DialogTitle>
            <DialogDescription className="text-slate-500">Add to your home screen for quick access</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-gk-bg rounded-lg">
              <div className="w-8 h-8 rounded-full bg-gk-navy flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">1</div>
              <div>
                <p className="font-medium text-slate-900">Tap the Share button</p>
                <p className="text-sm text-slate-500 mt-0.5">Look for the <Share className="w-4 h-4 inline-block mx-0.5" /> icon at the bottom of Safari</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gk-bg rounded-lg">
              <div className="w-8 h-8 rounded-full bg-gk-navy flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">2</div>
              <div>
                <p className="font-medium text-slate-900">Tap "Add to Home Screen"</p>
                <p className="text-sm text-slate-500 mt-0.5">Scroll down in the share menu to find this option</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gk-bg rounded-lg">
              <div className="w-8 h-8 rounded-full bg-gk-navy flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">3</div>
              <div>
                <p className="font-medium text-slate-900">Tap "Add"</p>
                <p className="text-sm text-slate-500 mt-0.5">The app will appear on your home screen</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
